import React, { createContext, useContext, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Tour context — a tiny state machine.
 *
 * State:
 *   - active     : boolean — is the tour running?
 *   - stepIndex  : number  — index into the storyboard
 *
 * Actions:
 *   - start()          — begin the tour at step 0 and navigate to its path
 *   - next()           — advance one step (or finish if at the end)
 *   - prev()           — go back one step (no-op on step 0)
 *   - jump(index)      — jump to a specific step (used when a step's target is missing)
 *   - close(returnTo)  — stop the tour and optionally navigate somewhere
 *
 * The engine (DemoTour.jsx) reads (active, stepIndex) and drives the
 * spotlight overlay. React Router owns the URL; the tour just calls
 * `navigate(path)` when the current step's path differs from the URL.
 */
const DemoTourContext = createContext(null);

export function DemoTourProvider({ children }) {
  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const nav = useNavigate();

  const start = useCallback((firstPath) => {
    setStepIndex(0);
    setActive(true);
    if (firstPath) nav(firstPath);
  }, [nav]);

  const next = useCallback((total) => {
    setStepIndex((i) => (i + 1 >= total ? i : i + 1));
  }, []);

  const prev = useCallback(() => {
    setStepIndex((i) => (i <= 0 ? 0 : i - 1));
  }, []);

  const jump = useCallback((idx) => setStepIndex(idx), []);

  const close = useCallback((returnTo) => {
    setActive(false);
    setStepIndex(0);
    if (returnTo) nav(returnTo);
  }, [nav]);

  const value = { active, stepIndex, start, next, prev, jump, close };
  return (
    <DemoTourContext.Provider value={value}>
      {children}
    </DemoTourContext.Provider>
  );
}

export function useDemoTour() {
  const ctx = useContext(DemoTourContext);
  if (!ctx) {
    // Return a no-op stub so components can call `useDemoTour()`
    // outside the provider (e.g. in a test) without crashing.
    return {
      active: false, stepIndex: 0,
      start: () => {}, next: () => {}, prev: () => {},
      jump: () => {}, close: () => {},
    };
  }
  return ctx;
}
